import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import s from './adminStyles';
import { authHeaders } from '../../context/AuthContext';
import MarkdownEditor from '../MarkdownEditor';
import CATEGORIES, { CATEGORY_BY_ID } from '../../config/blogCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  title: '',
  content: '',
  excerpt: '',
  slug: '',
  cover_image: '',
  title_en: '',
  content_en: '',
  excerpt_en: '',
  category: '',
  tags: [],
  featured: false,
};

const BlogForm = () => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(empty);

  useEffect(() => { load(); }, []);

  const load = () => {
    fetch(`${API_URL}/api/blog`)
      .then(r => r.json())
      .then(setPosts)
      .catch(console.error);
  };

  const reset = () => { setForm(empty); setEditingItem(null); };

  const startEdit = (post) => {
    setEditingItem(post);
    setForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      slug: post.slug,
      cover_image: post.cover_image,
      title_en: post.title_en || '',
      content_en: post.content_en || '',
      excerpt_en: post.excerpt_en || '',
      category: post.category || '',
      tags: post.tags || [],
      featured: !!post.featured,
    });
    setShowForm(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // Final sanitization safety net — even if the user never blurred the slug
    // field, we still never POST a slug with "SLUG " prefix or spaces.
    const cleanSlug = slugify(form.slug);
    // Polish-only deployment: mirror the Polish text into the *_en fields so
    // backend validation and blog search (which also scans *_en) keep working.
    const payload = {
      ...form,
      slug: cleanSlug,
      title_en: form.title,
      excerpt_en: form.excerpt,
      content_en: form.content,
    };
    if (cleanSlug !== form.slug) setForm(f => ({ ...f, slug: cleanSlug }));
    const url = editingItem
      ? `${API_URL}/api/blog/${editingItem.id}`
      : `${API_URL}/api/blog`;
    const method = editingItem ? 'PUT' : 'POST';
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    })
      .then(r => r.json())
      .then(() => { load(); reset(); setShowForm(false); })
      .catch(console.error);
  };

  const onDelete = (id) => {
    if (!window.confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;
    fetch(`${API_URL}/api/blog/${id}`, { method: 'DELETE', headers: authHeaders() })
      .then(load)
      .catch(console.error);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  /**
   * Slugify user input so pasted content like "SLUG: waarom-mislukken..."
   * (frequent copy-paste artefact from ChatGPT drafts) becomes a clean,
   * URL-safe kebab-case slug. Applied on blur + right before submit so the
   * user still sees what they type while editing, but the DB never gets
   * "SLUG  waarom-..." style garbage.
   */
  const slugify = (raw) => {
    if (!raw) return '';
    let out = String(raw)
      // Strip common labels ChatGPT prepends: "SLUG:", "slug -", "URL:", etc.
      .replace(/^\s*(slug|url)\s*[:\-]?\s*/i, '')
      // Diacritics → ASCII
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      // Spaces + underscores → hyphens
      .replace(/[\s_]+/g, '-')
      // Drop anything not alnum or hyphen
      .replace(/[^a-z0-9-]/g, '')
      // Collapse multi hyphens
      .replace(/-+/g, '-')
      // Trim leading/trailing hyphens
      .replace(/^-+|-+$/g, '');
    return out;
  };

  const onSlugBlur = () => {
    const cleaned = slugify(form.slug);
    if (cleaned !== form.slug) setForm(f => ({ ...f, slug: cleaned }));
  };

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.sectionTitle}>Blog Beheer</h2>
        <button
          style={s.btnPrimary}
          onClick={() => { setShowForm(!showForm); reset(); }}
          data-testid="add-blog-button"
        >
          <Plus size={20} />
          Nieuw artikel
        </button>
      </div>

      {showForm && (
        <form style={s.form} onSubmit={onSubmit} data-testid="blog-form">
          <div style={s.langSection}>
            <h3 style={s.langSectionTitle}>🇵🇱 Polski</h3>
            <div style={s.formGroup}>
              <label style={s.label}>Tytuł</label>
              <input style={s.input} type="text" value={form.title} onChange={set('title')} required data-testid="blog-title-input" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Zajawka (excerpt)</label>
              <textarea style={s.input} value={form.excerpt} onChange={set('excerpt')} required data-testid="blog-excerpt-input" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Treść</label>
              <MarkdownEditor
                value={form.content}
                onChange={(v) => setForm(f => ({ ...f, content: v }))}
                testId="blog-content-editor-pl"
              />
            </div>
          </div>

          <div style={s.formGroup}>
            <label style={s.label}>Slug (URL)</label>
            <input
              style={s.input}
              type="text"
              value={form.slug}
              onChange={set('slug')}
              onBlur={onSlugBlur}
              placeholder="huis-kopen-spanje"
              required
              data-testid="blog-slug-input"
            />
            <p style={{ color: '#8C8B88', fontSize: '0.78rem', margin: '0.35rem 0 0' }}>
              Alleen kleine letters, cijfers en koppeltekens. &quot;SLUG:&quot; of &quot;URL:&quot; labels worden automatisch verwijderd.
            </p>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Cover afbeelding URL</label>
            <input style={s.input} type="url" value={form.cover_image} onChange={set('cover_image')} required />
          </div>

          {/* Taxonomy — drives index categorisation + related-post matching */}
          <div style={s.formGroup}>
            <label style={s.label}>Categorie</label>
            <select
              style={s.input}
              value={form.category}
              onChange={set('category')}
              data-testid="blog-category-select"
            >
              <option value="">— Geen categorie —</option>
              {CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {form.category && CATEGORY_BY_ID[form.category]?.tagSuggestions && (
              <p style={{ ...s.label, marginTop: '0.4rem', fontWeight: 400, color: '#8C8B88' }}>
                Tag-suggesties: {CATEGORY_BY_ID[form.category].tagSuggestions.join(', ')}
              </p>
            )}
          </div>

          <div style={s.formGroup}>
            <label style={s.label}>Tags (komma-gescheiden)</label>
            <input
              style={s.input}
              type="text"
              value={(form.tags || []).join(', ')}
              onChange={(e) =>
                setForm(f => ({
                  ...f,
                  tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
                }))
              }
              placeholder="hypotheek, costa blanca, kosten koper"
              data-testid="blog-tags-input"
            />
          </div>

          <div style={s.formGroup}>
            <label style={{ ...s.label, display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm(f => ({ ...f, featured: e.target.checked }))}
                data-testid="blog-featured-checkbox"
              />
              <Star size={14} color="#C5A059" />
              Uitgelicht artikel (verschijnt bovenaan)
            </label>
          </div>

          <div style={s.formActions}>
            <button type="submit" style={s.btnPrimary} data-testid="submit-blog-button">
              {editingItem ? 'Bijwerken' : 'Publiceren'}
            </button>
            <button
              type="button"
              style={s.btnSecondary}
              onClick={() => { setShowForm(false); reset(); }}
            >
              Annuleren
            </button>
          </div>
        </form>
      )}

      <div style={s.table}>
        {posts.map(post => (
          <div key={post.id} style={s.tableRow} data-testid="blog-row">
            <div style={s.tableCell}>
              <img src={post.cover_image} alt={post.title} style={s.thumbnail} />
            </div>
            <div style={s.tableCell}>
              <strong>{post.title}</strong>
              <p style={s.cellSubtext}>{post.excerpt.substring(0, 60)}...</p>
            </div>
            <div style={s.tableCell}>{post.author}</div>
            <div style={s.tableCell}>
              <button style={s.iconButton} onClick={() => startEdit(post)} data-testid="edit-blog-button">
                <Edit size={18} />
              </button>
              <button
                style={{ ...s.iconButton, ...s.deleteButton }}
                onClick={() => onDelete(post.id)}
                data-testid="delete-blog-button"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogForm;
